import { useCallback, useEffect, useMemo, useState } from 'react';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import { useSeatUsage, type SubscriptionTier } from './useSeatUsage';

/**
 * Parse the structured `{error, hint?, detail?}` envelope our edge functions
 * return on 4xx/5xx. `supabase.functions.invoke` surfaces the failure as a
 * `FunctionsHttpError` whose `.message` is the generic
 * `"Edge Function returned a non-2xx status code"` — useless for users and
 * debugging alike. The actual body has the typed error code we want to show.
 *
 * Returns a best-effort human string. Falls back to the generic message when
 * the body isn't JSON or doesn't carry `error`.
 */
async function extractEdgeFunctionError(
  fnErr: unknown,
  fallback: string
): Promise<{ code: string | null; message: string }> {
  if (fnErr instanceof FunctionsHttpError) {
    try {
      const body = await fnErr.context.json();
      const code =
        body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string'
          ? ((body as { error: string }).error)
          : null;
      const detail =
        body && typeof body === 'object' && typeof (body as { detail?: unknown }).detail === 'string'
          ? ((body as { detail: string }).detail)
          : null;
      const hint =
        body && typeof body === 'object' && typeof (body as { hint?: unknown }).hint === 'string'
          ? ((body as { hint: string }).hint)
          : null;
      if (code) {
        // Friendly mapping for the codes the page is most likely to surface.
        const friendly: Record<string, string> = {
          subscription_incomplete:
            "Your previous checkout didn't finish. Pick a plan again to restart.",
          subscription_already_exists:
            'A subscription already exists for this org.',
          seats_over_new_tier:
            "You have more active members than the new tier allows. Remove members first.",
          forbidden: "You don't have permission to manage billing for this org.",
          forbidden_suspended:
            'Your account is suspended; ask an Owner to reactivate it.',
          not_authenticated: 'Please sign in again.',
          downgrade_not_requested:
            'Downgrade flow is out of sync — refresh and try again.',
          suspension_list_mismatch:
            'Member-to-suspend list changed — refresh and try again.',
          same_tier: "You're already on this plan.",
          use_cancel_for_free: 'Use Cancel to drop to Free at period end.',
          tier_config_missing:
            'Pricing data is unavailable right now. Try again in a moment.',
          card_declined:
            'Your card was declined. Update your payment method and try again.',
          invoice_unpaid:
            "Stripe couldn't collect payment for the upgrade. Update your payment method and try again.",
          stripe_preview_failed:
            "Couldn't preview the price change. Try again in a moment."
        };
        const human = friendly[code] ?? detail ?? hint ?? code;
        return { code, message: human };
      }
    } catch {
      // Body wasn't JSON or .json() threw — fall through to fallback.
    }
  }
  return { code: null, message: fallback };
}

export type { SubscriptionTier } from './useSeatUsage';

export type SubscriptionStatus =
  | 'free'
  | 'incomplete'
  | 'incomplete_expired'
  | 'active'
  | 'past_due'
  | 'unpaid'
  | 'canceled';

/** Derived UI state, narrower than the raw `subscriptions.status` enum so the
 *  view can switch on a single value instead of cross-referencing
 *  `status` + `cancel_at_period_end` + `scheduled_tier`. */
export type BillingState =
  | 'free'
  | 'active'
  | 'scheduled_downgrade'
  | 'scheduled_cancel'
  | 'past_due'
  | 'canceled'
  | 'processing';

export interface SubscriptionRow {
  org_id: string;
  current_tier: SubscriptionTier;
  status: SubscriptionStatus;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  scheduled_tier: SubscriptionTier | null;
  scheduled_change_at: string | null;
  pending_suspension_user_ids: ReadonlyArray<string>;
  latest_invoice_id: string | null;
  latest_invoice_status: string | null;
  latest_invoice_paid_at: string | null;
  default_payment_method_id: string | null;
}

export interface TierConfig {
  tier: SubscriptionTier;
  max_seats: number;
  monthly_price_usd: number;
  stripe_price_id: string | null;
}

export type BillingActionStatus =
  | 'idle'
  | 'creating'
  | 'updating'
  | 'canceling'
  | 'resuming'
  | 'polling';

export interface CreateSubscriptionResult {
  ok: boolean;
  client_secret?: string;
  subscription_id?: string;
  error?: string;
}

export interface UpdateTierResult {
  ok: boolean;
  scheduled?: boolean;
  current_tier?: SubscriptionTier;
  error?: string;
}

export interface PreviewTierChangeResult {
  ok: boolean;
  /** 'upgrade' or 'downgrade' — what the edge function decided based on
   *  tier ordinal. */
  direction?: 'upgrade' | 'downgrade';
  /** Cents. For an upgrade this is what the user will be charged today. */
  proration_amount?: number;
  /** Cents. The full monthly amount the next renewal will invoice. */
  next_invoice_total?: number;
  /** ISO timestamp of the current period end (when the next bill lands). */
  next_period_end?: string | null;
  /** Whole days until `next_period_end`. */
  days_remaining?: number;
  currency?: string;
  error?: string;
}

export interface CancelOrResumeResult {
  ok: boolean;
  cancel_at_period_end?: boolean;
  error?: string;
}

export interface PollResult {
  ok: boolean;
  reason?: 'timeout';
}

export interface UseBillingVM {
  /** Active org id passed in by the consumer. Mirrored back so the page can
   *  guard its render against the same id the VM is wired to. */
  orgId: string | null;
  /** The single `subscriptions` row for `orgId`, or `null` until loaded. */
  subscription: SubscriptionRow | null;
  /** Module-level cached tier-config table (5 rows). Ordered ascending by
   *  `max_seats` so the pricing grid renders Free → $500 naturally. */
  tierConfig: ReadonlyArray<TierConfig>;
  /** Live seat-usage snapshot from `org_seat_usage(p_org_id)`. */
  seatUsage: ReturnType<typeof useSeatUsage>;
  /** Derived UI state — see `BillingState`. `'processing'` reflects the
   *  in-flight checkout (`actionStatus === 'creating' | 'polling'`) so the
   *  page can render the spinner without inspecting `actionStatus` directly. */
  state: BillingState;
  /** Initial-load lifecycle. */
  loading: boolean;
  /** Top-level load error (subscription row / tier config / seat usage). */
  error: string | null;
  /** Most-recent action's lifecycle. Use alongside `state` for fine-grained
   *  spinners (e.g. disable a TierCard's Upgrade CTA while
   *  `actionStatus === 'updating'`). */
  actionStatus: BillingActionStatus;
  /** Most-recent action's error envelope, surfaced when an edge function
   *  returned `{ok: false}` or a network call threw. Cleared on next action. */
  actionError: string | null;
  /** Refetch the `subscriptions` row (after a Stripe action settles). */
  refetchSubscription: () => Promise<void>;
  /** Reset `actionStatus` to `'idle'` + clear `actionError`. The
   *  `createSubscription` flow deliberately stays in `'creating'` until the
   *  caller drives the PaymentElement to confirmation + polls — but if the
   *  user dismisses the checkout modal first, the VM would otherwise stay
   *  pinned at `'creating'` forever and the tier grid would render all CTAs
   *  as disabled. Modal `onClose` should call this. */
  resetAction: () => void;
  /** Create a new subscription for `targetTier`. Returns the `{client_secret,
   *  subscription_id}` envelope the caller hands to `<PaymentElement>` for
   *  confirmation. */
  createSubscription: (args: {
    tier: SubscriptionTier;
  }) => Promise<CreateSubscriptionResult>;
  /** Preview the prorated invoice for a tier change before the user
   *  confirms. Powers the confirmation step inside the checkout modal. */
  previewTierChange: (args: {
    newTier: SubscriptionTier;
  }) => Promise<PreviewTierChangeResult>;
  /** Upgrade or downgrade. For downgrade-with-overage, the caller passes
   *  `membersToSuspend` (uuids); the edge function re-validates this against
   *  `subscriptions.pending_suspension_user_ids` written by the
   *  `request_tier_downgrade` RPC, so the call here ALSO assumes the RPC has
   *  already been invoked (or is a no-op upgrade). */
  updateTier: (args: {
    newTier: SubscriptionTier;
    membersToSuspend?: ReadonlyArray<string>;
  }) => Promise<UpdateTierResult>;
  /** Sets `cancel_at_period_end = true` in Stripe + DB. Reversible via
   *  `resumeSubscription()` until `current_period_end` passes. */
  cancelSubscription: () => Promise<CancelOrResumeResult>;
  /** Clears a pending cancellation if `current_period_end` hasn't passed
   *  yet. Edge function returns `{ok: false}` past the boundary; the page
   *  surfaces that via `actionError`. */
  resumeSubscription: () => Promise<CancelOrResumeResult>;
  /** Poll `subscriptions.status === 'active'` every 1.5s up to 20 attempts
   *  (~30s wall clock). Resolves `{ok: true}` on the first `active` read,
   *  `{ok: false, reason: 'timeout'}` after the last attempt. Side-effect:
   *  the final read also updates `subscription` so the UI sees the new
   *  `current_tier` without an additional refetch. */
  pollUntilActive: () => Promise<PollResult>;
}

// Module-level cache for the small reference table — 5 rows, never mutated
// at runtime. Fetched once on first VM mount and reused on every subsequent
// mount until the page reloads.
let tierConfigCache: ReadonlyArray<TierConfig> | null = null;
let tierConfigInflight: Promise<ReadonlyArray<TierConfig>> | null = null;

async function loadTierConfig(): Promise<ReadonlyArray<TierConfig>> {
  if (tierConfigCache) return tierConfigCache;
  if (tierConfigInflight) return tierConfigInflight;
  tierConfigInflight = (async () => {
    const { data, error: err } = await supabase
      .from('subscription_tier_config')
      .select('tier, max_seats, monthly_price_usd, stripe_price_id')
      .order('max_seats', { ascending: true });
    if (err || !data) {
      tierConfigInflight = null;
      throw err ?? new Error('subscription_tier_config returned no rows');
    }
    const rows = (data as ReadonlyArray<{
      tier: string;
      max_seats: number;
      monthly_price_usd: number;
      stripe_price_id: string | null;
    }>).map<TierConfig>((r) => ({
      tier: r.tier as SubscriptionTier,
      max_seats: r.max_seats,
      monthly_price_usd: r.monthly_price_usd,
      stripe_price_id: r.stripe_price_id ?? null
    }));
    tierConfigCache = rows;
    tierConfigInflight = null;
    return rows;
  })();
  return tierConfigInflight;
}

function normalizeStatus(s: string | null | undefined): SubscriptionStatus {
  if (
    s === 'free' ||
    s === 'incomplete' ||
    s === 'incomplete_expired' ||
    s === 'active' ||
    s === 'past_due' ||
    s === 'unpaid' ||
    s === 'canceled'
  ) {
    return s;
  }
  return 'free';
}

function normalizeTier(t: string | null | undefined): SubscriptionTier {
  if (
    t === 'free' ||
    t === 't25' ||
    t === 't100' ||
    t === 't200' ||
    t === 't500'
  ) {
    return t;
  }
  return 'free';
}

interface RawSubscriptionRow {
  org_id?: string | null;
  current_tier?: string | null;
  status?: string | null;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  current_period_start?: string | null;
  current_period_end?: string | null;
  cancel_at_period_end?: boolean | null;
  scheduled_tier?: string | null;
  scheduled_change_at?: string | null;
  pending_suspension_user_ids?: string[] | null;
  latest_invoice_id?: string | null;
  latest_invoice_status?: string | null;
  latest_invoice_paid_at?: string | null;
  default_payment_method_id?: string | null;
}

function normalizeSubscription(
  raw: RawSubscriptionRow | null | undefined,
  fallbackOrgId: string
): SubscriptionRow | null {
  if (!raw) return null;
  return {
    org_id: raw.org_id ?? fallbackOrgId,
    current_tier: normalizeTier(raw.current_tier),
    status: normalizeStatus(raw.status),
    stripe_customer_id: raw.stripe_customer_id ?? null,
    stripe_subscription_id: raw.stripe_subscription_id ?? null,
    current_period_start: raw.current_period_start ?? null,
    current_period_end: raw.current_period_end ?? null,
    cancel_at_period_end: raw.cancel_at_period_end === true,
    scheduled_tier: raw.scheduled_tier
      ? normalizeTier(raw.scheduled_tier)
      : null,
    scheduled_change_at: raw.scheduled_change_at ?? null,
    pending_suspension_user_ids: Array.isArray(raw.pending_suspension_user_ids)
      ? raw.pending_suspension_user_ids
      : [],
    latest_invoice_id: raw.latest_invoice_id ?? null,
    latest_invoice_status: raw.latest_invoice_status ?? null,
    latest_invoice_paid_at: raw.latest_invoice_paid_at ?? null,
    default_payment_method_id: raw.default_payment_method_id ?? null
  };
}

function deriveState(
  sub: SubscriptionRow | null,
  actionStatus: BillingActionStatus
): BillingState {
  if (actionStatus === 'creating' || actionStatus === 'polling') {
    return 'processing';
  }
  if (!sub) return 'free';
  if (sub.status === 'canceled') return 'canceled';
  if (sub.status === 'past_due' || sub.status === 'unpaid') return 'past_due';
  if (sub.status === 'free') return 'free';
  if (sub.cancel_at_period_end) return 'scheduled_cancel';
  if (sub.scheduled_tier && sub.scheduled_tier !== sub.current_tier) {
    return 'scheduled_downgrade';
  }
  if (sub.status === 'active') return 'active';
  // incomplete / incomplete_expired land here; the checkout modal owns those
  // surfaces via `actionStatus === 'polling'`, so when we see them in steady
  // state we treat them as `processing` (the user backgrounded the tab mid-
  // checkout, say).
  return 'processing';
}

const POLL_INTERVAL_MS = 1_500;
const POLL_MAX_ATTEMPTS = 20;

/**
 * ViewModel for the `/settings/billing` page. Combines the `subscriptions`
 * row, the (module-cached) `subscription_tier_config` table, and inline
 * `useSeatUsage` for the seat-usage meter. Suspended-member list is exposed
 * separately via `useSuspendedMembers(orgId)` — the consumer calls it lazily
 * (the downgrade-overage step) so users never on that path don't pay the
 * RPC round-trip.
 *
 * Action functions invoke the matching edge function via
 * `supabase.functions.invoke('stripe-<name>', { body: { org_id, ... } })`.
 * Edge functions return `{ok, ...}` envelopes; this VM threads them through
 * unchanged so callers can branch on `result.ok` without re-parsing.
 *
 * @param orgId — active org id. `null` keeps the VM in a noop-loading state;
 *   the consumer typically passes `activeOrgId` from the shell session.
 */
export function useBillingVM(orgId: string | null): UseBillingVM {
  const [subscription, setSubscription] = useState<SubscriptionRow | null>(
    null
  );
  const [tierConfig, setTierConfig] = useState<ReadonlyArray<TierConfig>>(
    tierConfigCache ?? []
  );
  const [loading, setLoading] = useState<boolean>(!!orgId);
  const [error, setError] = useState<string | null>(null);
  const [actionStatus, setActionStatus] = useState<BillingActionStatus>('idle');
  const [actionError, setActionError] = useState<string | null>(null);

  const seatUsage = useSeatUsage(orgId);

  const loadSubscription = useCallback(
    async (id: string): Promise<SubscriptionRow | null> => {
      const { data, error: err } = await supabase
        .from('subscriptions')
        .select(
          'org_id, current_tier, status, stripe_customer_id, stripe_subscription_id, current_period_start, current_period_end, cancel_at_period_end, scheduled_tier, scheduled_change_at, pending_suspension_user_ids, latest_invoice_id, latest_invoice_status, latest_invoice_paid_at, default_payment_method_id'
        )
        .eq('org_id', id)
        .maybeSingle();
      if (err) {
        setError("Couldn't load your subscription. Please try again.");
        return null;
      }
      return normalizeSubscription(data as RawSubscriptionRow | null, id);
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    if (!orgId) {
      setSubscription(null);
      setLoading(false);
      setError(null);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    setError(null);
    (async () => {
      const [subRow, cfgRows] = await Promise.all([
        loadSubscription(orgId),
        loadTierConfig().catch(() => null)
      ]);
      if (cancelled) return;
      setSubscription(subRow);
      if (cfgRows) setTierConfig(cfgRows);
      else if (!tierConfigCache) {
        setError("Couldn't load pricing. Please try again.");
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, loadSubscription]);

  const refetchSubscription = useCallback(async () => {
    if (!orgId) return;
    const row = await loadSubscription(orgId);
    setSubscription(row);
  }, [orgId, loadSubscription]);

  const createSubscription = useCallback(
    async ({
      tier
    }: {
      tier: SubscriptionTier;
    }): Promise<CreateSubscriptionResult> => {
      if (!orgId) return { ok: false, error: 'no_active_org' };
      setActionStatus('creating');
      setActionError(null);
      const { data, error: fnErr } = await supabase.functions.invoke(
        'stripe-create-subscription',
        { body: { org_id: orgId, tier } }
      );
      if (fnErr) {
        const parsed = await extractEdgeFunctionError(
          fnErr,
          "Couldn't start checkout. Please try again."
        );
        setActionStatus('idle');
        setActionError(parsed.message);
        // Return the human message in `error` so consumers like
        // BillingCheckoutModal can display it directly. The structured `code`
        // is left for callers that need to branch on it via `actionStatus`
        // / future shape changes; today, all consumers just render the string.
        return { ok: false, error: parsed.message };
      }
      const result = (data ?? {}) as CreateSubscriptionResult;
      if (!result.ok) {
        setActionStatus('idle');
        setActionError(
          "Couldn't start checkout. Please try again."
        );
        return result;
      }
      // Stay in 'creating' — the caller will mount <PaymentElement> with the
      // client_secret and then call `pollUntilActive()`, which transitions us
      // to 'polling' → 'idle'.
      return result;
    },
    [orgId]
  );

  const previewTierChange = useCallback(
    async ({
      newTier
    }: {
      newTier: SubscriptionTier;
    }): Promise<PreviewTierChangeResult> => {
      if (!orgId) return { ok: false, error: 'no_active_org' };
      const { data, error: fnErr } = await supabase.functions.invoke(
        'stripe-preview-tier-change',
        { body: { org_id: orgId, new_tier: newTier } }
      );
      if (fnErr) {
        const parsed = await extractEdgeFunctionError(
          fnErr,
          "Couldn't preview the price change. Try again in a moment."
        );
        return { ok: false, error: parsed.message };
      }
      const result = (data ?? {}) as PreviewTierChangeResult;
      if (!result.ok) {
        return { ok: false, error: result.error };
      }
      return result;
    },
    [orgId]
  );

  const updateTier = useCallback(
    async ({
      newTier,
      membersToSuspend
    }: {
      newTier: SubscriptionTier;
      membersToSuspend?: ReadonlyArray<string>;
    }): Promise<UpdateTierResult> => {
      if (!orgId) return { ok: false, error: 'no_active_org' };
      setActionStatus('updating');
      setActionError(null);
      const body: Record<string, unknown> = {
        org_id: orgId,
        new_tier: newTier
      };
      if (membersToSuspend && membersToSuspend.length > 0) {
        body.members_to_suspend = membersToSuspend;
      }
      const { data, error: fnErr } = await supabase.functions.invoke(
        'stripe-update-subscription',
        { body }
      );
      if (fnErr) {
        const parsed = await extractEdgeFunctionError(
          fnErr,
          "Couldn't update your plan. Please try again."
        );
        setActionStatus('idle');
        setActionError(parsed.message);
        // Return the human message in `error` so consumers like
        // BillingCheckoutModal can display it directly. The structured `code`
        // is left for callers that need to branch on it via `actionStatus`
        // / future shape changes; today, all consumers just render the string.
        return { ok: false, error: parsed.message };
      }
      const result = (data ?? {}) as UpdateTierResult;
      if (!result.ok) {
        setActionStatus('idle');
        setActionError("Couldn't update your plan. Please try again.");
        return result;
      }
      await refetchSubscription();
      await seatUsage.refetch();
      setActionStatus('idle');
      return result;
    },
    [orgId, refetchSubscription, seatUsage]
  );

  const cancelSubscription =
    useCallback(async (): Promise<CancelOrResumeResult> => {
      if (!orgId) return { ok: false, error: 'no_active_org' };
      setActionStatus('canceling');
      setActionError(null);
      const { data, error: fnErr } = await supabase.functions.invoke(
        'stripe-cancel-subscription',
        { body: { org_id: orgId } }
      );
      if (fnErr) {
        const parsed = await extractEdgeFunctionError(
          fnErr,
          "Couldn't cancel your subscription. Please try again."
        );
        setActionStatus('idle');
        setActionError(parsed.message);
        // Return the human message in `error` so consumers like
        // BillingCheckoutModal can display it directly. The structured `code`
        // is left for callers that need to branch on it via `actionStatus`
        // / future shape changes; today, all consumers just render the string.
        return { ok: false, error: parsed.message };
      }
      const result = (data ?? {}) as CancelOrResumeResult;
      if (!result.ok) {
        setActionStatus('idle');
        setActionError("Couldn't cancel your subscription. Please try again.");
        return result;
      }
      await refetchSubscription();
      setActionStatus('idle');
      return result;
    }, [orgId, refetchSubscription]);

  const resumeSubscription =
    useCallback(async (): Promise<CancelOrResumeResult> => {
      if (!orgId) return { ok: false, error: 'no_active_org' };
      setActionStatus('resuming');
      setActionError(null);
      const { data, error: fnErr } = await supabase.functions.invoke(
        'stripe-resume-subscription',
        { body: { org_id: orgId } }
      );
      if (fnErr) {
        const parsed = await extractEdgeFunctionError(
          fnErr,
          "Couldn't resume your subscription. Please try again."
        );
        setActionStatus('idle');
        setActionError(parsed.message);
        // Return the human message in `error` so consumers like
        // BillingCheckoutModal can display it directly. The structured `code`
        // is left for callers that need to branch on it via `actionStatus`
        // / future shape changes; today, all consumers just render the string.
        return { ok: false, error: parsed.message };
      }
      const result = (data ?? {}) as CancelOrResumeResult;
      if (!result.ok) {
        setActionStatus('idle');
        setActionError("Couldn't resume your subscription. Please try again.");
        return result;
      }
      await refetchSubscription();
      setActionStatus('idle');
      return result;
    }, [orgId, refetchSubscription]);

  const pollUntilActive = useCallback(async (): Promise<PollResult> => {
    if (!orgId) return { ok: false, reason: 'timeout' };
    setActionStatus('polling');
    setActionError(null);
    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
      // Wait first so we don't re-read the row immediately after the caller's
      // own pre-poll refetch returned `incomplete` — gives Stripe + the
      // webhook a chance to fire.
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      const row = await loadSubscription(orgId);
      if (row) {
        setSubscription(row);
        if (row.status === 'active') {
          // Refresh seat usage too — tier change may have lifted the cap.
          await seatUsage.refetch();
          setActionStatus('idle');
          return { ok: true };
        }
      }
    }
    setActionStatus('idle');
    return { ok: false, reason: 'timeout' };
  }, [orgId, loadSubscription, seatUsage]);

  const state = useMemo(
    () => deriveState(subscription, actionStatus),
    [subscription, actionStatus]
  );

  const resetAction = useCallback(() => {
    setActionStatus('idle');
    setActionError(null);
  }, []);

  return {
    orgId,
    subscription,
    tierConfig,
    seatUsage,
    state,
    loading,
    error,
    actionStatus,
    actionError,
    refetchSubscription,
    resetAction,
    createSubscription,
    previewTierChange,
    updateTier,
    cancelSubscription,
    resumeSubscription,
    pollUntilActive
  };
}
